package com.heelion.farmafreelance.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import farmafreelance.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import com.heelion.farmafreelance.models.AppThemeColor

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsScreen(
    onBack: () -> Unit,
    currentLanguage: String,
    onLanguageChange: (String) -> Unit,
    selectedColor: AppThemeColor = AppThemeColor.GREEN,
    onColorChange: (AppThemeColor) -> Unit = {},
    /** Con un utente collegato compare anche la verifica in due passaggi. */
    viewModel: AuthViewModel? = null
) {
    val languages = listOf(
        "🇮🇹 Italiano" to "it",
        "🇬🇧 English" to "en",
        "🇪🇸 Español" to "es",
        "🇫🇷 Français" to "fr",
        "🇩🇪 Deutsch" to "de",
        "🇵🇹 Português" to "pt",
        "🇬🇷 Ελληνικά" to "el",
        "🇷🇺 Русский" to "ru",
        "🇨🇳 中文" to "zh"
    )

    Scaffold(
        containerColor = Color.Transparent,
        topBar = {
            TopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent),
                title = { Text(stringResource(Res.string.settings_title)) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(
                            imageVector = AppIcons.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .padding(16.dp)
                .verticalScroll(rememberScrollState())
        ) {
            Text(
                text = stringResource(Res.string.select_language),
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.primary,
                modifier = Modifier.padding(bottom = 16.dp)
            )

            var expandedLang by remember { mutableStateOf(false) }
            val currentLangName = languages.find { it.second == currentLanguage }?.first ?: "Italiano"

            ExposedDropdownMenuBox(
                expanded = expandedLang,
                onExpandedChange = { expandedLang = !expandedLang },
                modifier = Modifier.fillMaxWidth()
            ) {
                OutlinedTextField(
                    value = currentLangName,
                    onValueChange = {},
                    readOnly = true,
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedLang) },
                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color.White
                    )
                )
                ExposedDropdownMenu(
                    expanded = expandedLang,
                    onDismissRequest = { expandedLang = false }
                ) {
                    languages.forEach { (name, code) ->
                        DropdownMenuItem(
                            text = { Text(name) },
                            onClick = {
                                onLanguageChange(code)
                                expandedLang = false
                            }
                        )
                    }
                }
            }
            
            Spacer(modifier = Modifier.height(32.dp))

            Text(
                text = "Colore Tema Dashboard",
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.primary,
                modifier = Modifier.padding(bottom = 16.dp)
            )

            Card(
                modifier = Modifier.fillMaxWidth(),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp),
                    horizontalArrangement = Arrangement.SpaceEvenly,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    AppThemeColor.entries.forEach { themeColor ->
                        val colorValue = Color(themeColor.hex.removePrefix("0x").toLong(16))
                        Box(
                            modifier = Modifier
                                .size(36.dp)
                                .background(colorValue, shape = RoundedCornerShape(18.dp))
                                .let { 
                                    if (colorValue == Color.White) {
                                        it.border(1.dp, Color.LightGray, shape = RoundedCornerShape(18.dp))
                                    } else it
                                }
                                .clickable { onColorChange(themeColor) }
                                .let {
                                    if (selectedColor == themeColor) {
                                        it.border(2.dp, MaterialTheme.colorScheme.primary, shape = RoundedCornerShape(18.dp))
                                    } else it
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            if (selectedColor == themeColor) {
                                Icon(
                                    imageVector = AppIcons.Check,
                                    contentDescription = null,
                                    tint = if (colorValue == Color.White) Color.Black else Color.White,
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                        }
                    }
                }
            }

            if (viewModel?.currentUserEmail != null) {
                Spacer(modifier = Modifier.height(32.dp))
                SezioneDueFattori(viewModel)
                Spacer(modifier = Modifier.height(32.dp))
                SezioneConsensi(viewModel)
                Spacer(modifier = Modifier.height(32.dp))
                SezioneEliminaAccount(viewModel, onEliminato = onBack)
            }

            Spacer(modifier = Modifier.height(24.dp))

            val uriHandler = androidx.compose.ui.platform.LocalUriHandler.current
            TextButton(
                onClick = { uriHandler.openUri(URL_PRIVACY) },
                modifier = Modifier.align(Alignment.CenterHorizontally)
            ) { Text("Informativa sulla privacy") }

            Text(
                text = "Version 1.0.0",
                style = MaterialTheme.typography.labelSmall,
                color = Color.Gray,
                modifier = Modifier.align(Alignment.CenterHorizontally)
            )
        }
    }
}
